import { createFeature, createReducer, on } from '@ngrx/store';
import { SortMeta, TableTask, Task } from '@qtek/shared/models';
import { TasksActions } from './tasks.actions';

export interface TasksMeta {
  search?: any[];
  ops?: any;
  properties?: any[];
  sort?: any[];
  loading?: boolean;
}

export interface TasksState {
  allTasks: Record<string, (Task | TableTask)[]>;
  loading: boolean;
  allTasksLoading: Record<string, boolean>;
  task: TableTask | null;
  taskLoading: boolean;
  taskError: Error | null;
  taskStatuses: any[];
  taskActions: any[];
  loadingTaskStatuses: boolean;
  loadingTaskActions: boolean;
  allTasksMeta: Record<string, TasksMeta>;
  queryParams: Record<string, any>;
  dealsForTask: { id: string; name: string }[];
}

export const tasksInitialState: TasksState = {
  allTasks: {},
  loading: false,
  allTasksLoading: {},
  task: null,
  taskLoading: false,
  taskError: null,
  taskStatuses: [],
  taskActions: [],
  loadingTaskStatuses: false,
  loadingTaskActions: false,
  allTasksMeta: {},
  queryParams: {},
  dealsForTask: [],
};

export const TasksReducer = createReducer(
  tasksInitialState,
  on(TasksActions.subscribeTasks, (state, { mysid, payload }) => ({
    ...state,
    loading: true,
    allTasksLoading: {
      ...state.allTasksLoading,
      [mysid]: true,
    },
    queryParams: {
      ...state.queryParams,
      [mysid]: payload?.prms || {},
    },
  })),
  on(TasksActions.unsubscribeTasks, (state, { mysid }) => {
    const { [mysid]: _, ...allTasks } = state.allTasks;
    const { [mysid]: __, ...allTasksMeta } = state.allTasksMeta;
    const { [mysid]: ___, ...allTasksLoading } = state.allTasksLoading;
    const { [mysid]: ____, ...queryParams } = state.queryParams;

    return {
      ...state,
      allTasks,
      allTasksMeta,
      allTasksLoading,
      queryParams,
    };
  }),
  on(TasksActions.loadTasksSuccess, (state, { tasks, mysid }) => ({
    ...state,
    allTasks: {
      ...state.allTasks,
      [mysid]: tasks,
    },
    loading: false,
    allTasksLoading: {
      ...state.allTasksLoading,
      [mysid]: false,
    },
  })),
  on(TasksActions.loadTasksFailure, (state, { error, mysid }) => ({
    ...state,
    loading: false,
    allTasksLoading: {
      ...state.allTasksLoading,
      [mysid]: false,
    },
  })),
  on(TasksActions.createTaskSuccess, (state, { task, mysid }) => {
    const queryParams = state.queryParams[mysid];
    const hasAssignedByMeFilter = queryParams?.assignedByMe === true;
    const hasOwnerTypePrsFilter = queryParams?.ownerType === 'prs';
    const hasIncludeAssignedFilter = queryParams?.includeAssigned === true;
    const isOutstandingTasksList =
      hasOwnerTypePrsFilter && hasIncludeAssignedFilter;
    const existingTasks = state.allTasks[mysid] || [];
    const taskAlreadyExists = existingTasks.some(t => t._id === task._id);

    // For "Tasks You've Assigned": if task was reassigned to creator (self), don't add it
    if (hasAssignedByMeFilter) {
      const authorId = task.params?.authorId?.[0];
      const isReassignedToSelf = authorId && task.usrId === authorId;

      if (isReassignedToSelf) {
        // Don't add the task - it was reassigned to self
        return {
          ...state,
          loading: false,
          allTasksLoading: {
            ...state.allTasksLoading,
            [mysid]: false,
          },
        };
      }

      // If task already exists, update it instead of adding a duplicate
      if (taskAlreadyExists) {
        return {
          ...state,
          allTasks: {
            ...state.allTasks,
            [mysid]: existingTasks.map(t => (t._id === task._id ? task : t)),
          },
          loading: false,
          allTasksLoading: {
            ...state.allTasksLoading,
            [mysid]: false,
          },
        };
      }
    }

    // For "Your Outstanding Tasks": if task was assigned to someone else, don't add it
    if (isOutstandingTasksList) {
      const authorId = task.params?.authorId?.[0];
      // "Your Outstanding Tasks" shows tasks assigned to the current user
      // If taskUsrId is different from authorId, it means it was assigned to someone else
      const isAssignedToSomeoneElse = authorId && task.usrId !== authorId;

      if (isAssignedToSomeoneElse) {
        // Don't add the task - it was assigned to someone else
        return {
          ...state,
          loading: false,
          allTasksLoading: {
            ...state.allTasksLoading,
            [mysid]: false,
          },
        };
      }

      // If task already exists, update it instead of adding a duplicate
      if (taskAlreadyExists) {
        return {
          ...state,
          allTasks: {
            ...state.allTasks,
            [mysid]: existingTasks.map(t => (t._id === task._id ? task : t)),
          },
          loading: false,
          allTasksLoading: {
            ...state.allTasksLoading,
            [mysid]: false,
          },
        };
      }
    }

    return {
      ...state,
      allTasks: {
        ...state.allTasks,
        [mysid]: [task, ...existingTasks],
      },
      loading: false,
      allTasksLoading: {
        ...state.allTasksLoading,
        [mysid]: false,
      },
    };
  }),
  on(TasksActions.createTaskFailure, (state, { mysid }) => ({
    ...state,
    loading: false,
    allTasksLoading: {
      ...state.allTasksLoading,
      [mysid]: false,
    },
  })),
  on(TasksActions.deleteTaskSuccess, (state, { task, mysid }) => {
    return {
      ...state,
      allTasks: {
        ...state.allTasks,
        [mysid]: (state.allTasks[mysid] || []).filter(t => t._id !== task._id),
      },
    };
  }),
  on(TasksActions.deleteTaskFailure, state => ({
    ...state,
  })),
  on(TasksActions.updateTaskSuccess, (state, { task, mysid }) => {
    // Update task in all active lists, not just the one that received the WebSocket message
    const allMysids = Object.keys(state.queryParams);
    const updatedAllTasks: Record<string, (Task | TableTask)[]> = {
      ...state.allTasks,
    };

    for (const currentMysid of allMysids) {
      const queryParams = state.queryParams[currentMysid];
      const hadStatusFilter = queryParams?.status === 'todo';
      const taskStatusChanged = task.status !== 'todo';
      const hasAssignedByMeFilter = queryParams?.assignedByMe === true;
      const existingTasks = state.allTasks[currentMysid] || [];

      // Remove task if status changed from 'todo'
      if (hadStatusFilter && taskStatusChanged) {
        updatedAllTasks[currentMysid] = existingTasks.filter(
          t => t._id !== task._id
        );
        continue;
      }

      const taskIndex = existingTasks.findIndex(t => t._id === task._id);
      let updatedTasks: (Task | TableTask)[];

      if (taskIndex !== -1) {
        const existingTask = existingTasks[taskIndex];
        const hasOwnerTypePrsFilter = queryParams?.ownerType === 'prs';
        const hasIncludeAssignedFilter = queryParams?.includeAssigned === true;
        const isOutstandingTasksList =
          hasOwnerTypePrsFilter && hasIncludeAssignedFilter;

        if (hasAssignedByMeFilter) {
          // Remove if reassigned to self (usrId === authorId)
          const authorId = task.params?.authorId?.[0];
          const isReassignedToSelf = authorId && task.usrId === authorId;
          updatedTasks = isReassignedToSelf
            ? existingTasks.filter(t => t._id !== task._id)
            : existingTasks.map(t => (t._id === task._id ? task : t));
        } else if (isOutstandingTasksList) {
          // Remove if usrId changed (reassigned to someone else)
          const usrIdChanged = existingTask.usrId !== task.usrId;
          updatedTasks = usrIdChanged
            ? existingTasks.filter(t => t._id !== task._id)
            : existingTasks.map(t => (t._id === task._id ? task : t));
        } else {
          updatedTasks = existingTasks.map(t =>
            t._id === task._id ? task : t
          );
        }
      } else {
        updatedTasks = existingTasks;
      }

      updatedAllTasks[currentMysid] = updatedTasks;
    }

    return {
      ...state,
      allTasks: updatedAllTasks,
    };
  }),
  on(TasksActions.updateTaskFailure, state => ({
    ...state,
  })),
  on(TasksActions.loadTasksMetaSuccess, (state, { meta, mysid }) => ({
    ...state,
    allTasksMeta: {
      ...state.allTasksMeta,
      [mysid]: meta,
    },
  })),
  on(TasksActions.loadTasksMetaFailure, state => ({
    ...state,
  })),
  on(TasksActions.subscribeSingleTask, (state, { taskId }) => {
    return {
      ...state,
      taskLoading: true,
      taskError: null as Error | null,
    };
  }),
  on(TasksActions.unsubscribeSingleTask, state => {
    return {
      ...state,
      task: null as TableTask | null,
      taskLoading: false,
      taskError: null as Error | null,
    };
  }),
  on(TasksActions.loadSingleTaskSuccess, (state, { task }) => {
    return {
      ...state,
      task: task as TableTask,
      taskLoading: false,
      taskError: null as Error | null,
    };
  }),
  on(TasksActions.loadSingleTaskFailure, (state, { error }) => {
    return {
      ...state,
      taskLoading: false,
      taskError: error,
    };
  }),
  on(TasksActions.getDealsForTaskSuccess, (state, { deals }) => {
    return {
      ...state,
      dealsForTask: deals,
    };
  })
);

export const TasksFeature = createFeature({
  name: 'tasks',
  reducer: TasksReducer,
  extraSelectors: ({ selectTasksState }) => ({
    selectTasksByMysid: (mysid: string) =>
      createSelector(
        selectTasksState,
        (state: TasksState) => state?.allTasks?.[mysid] || []
      ),
    selectTasksMetaByMysid: (mysid: string) =>
      createSelector(
        selectTasksState,
        (state: TasksState) => state?.allTasksMeta?.[mysid] || null
      ),
  }),
});

export const {
  selectAllTasks,
  selectLoading: selectTasksLoading,
  selectAllTasksLoading,
  selectTask,
  selectTaskLoading,
  selectTaskError,
  selectTaskStatuses,
  selectTaskActions,
  selectLoadingTaskStatuses,
  selectLoadingTaskActions,
  selectAllTasksMeta,
  selectTasksByMysid,
  selectTasksMetaByMysid,
  selectDealsForTask,
} = TasksFeature;

// Custom selectors for tasksMeta properties
import { createSelector } from '@ngrx/store';

export const selectTaskMetaSearchColumns = (mysid: string) =>
  createSelector(
    selectTasksMetaByMysid(mysid),
    (tasksMeta: TasksMeta | null) => tasksMeta?.search || []
  );

export const selectTaskMetaOperators = (mysid: string) =>
  createSelector(
    selectTasksMetaByMysid(mysid),
    (tasksMeta: TasksMeta | null) => tasksMeta?.ops || {}
  );

export const selectTaskMetaFilters = (mysid: string) =>
  createSelector(
    selectTasksMetaByMysid(mysid),
    (tasksMeta: TasksMeta | null) =>
      tasksMeta?.properties?.filter((prop: any) => prop.filter === true) || []
  );

export const selectTaskMetaSortColumns = (mysid: string) =>
  createSelector(
    selectTasksMetaByMysid(mysid),
    (tasksMeta: TasksMeta | null) => ({
      properties: tasksMeta?.properties || [],
      sort: new SortMeta(tasksMeta?.sort || []),
    })
  );

export const selectLoadingTaskMeta = (mysid: string) =>
  createSelector(
    selectTasksMetaByMysid(mysid),
    (tasksMeta: TasksMeta | null) => tasksMeta?.loading || false
  );

export const selectTasksLoadingByMysid = (mysid: string) =>
  createSelector(
    selectAllTasksLoading,
    (allTasksLoading: Record<string, boolean>) =>
      allTasksLoading[mysid] || false
  );
